import { computedFrom } from 'aurelia-binding';
import {
	EventAggregator,
	Subscription as EventSubscription,
} from 'aurelia-event-aggregator';
import { Container } from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';
import { Router } from 'aurelia-router';
import { StateHistory, Store } from 'aurelia-store';
import PerfectScrollbar from 'perfect-scrollbar';

import { AppState } from '~/app-store';
import { NavigationStore } from '~/components/navigation/navigation-types';
import { CommonURLParam, TREE_VIEW_SESSION_VARIABLE_NAME } from '~/constants';
import { Logger } from '~/utils/logger';

export enum ScrollEvent {
	SCROLLTOP = 'scrollTop',
}

export class BaseViewModel {
	/**
	 * The router instance
	 */
	protected router: Router;

	/**
	 * The I18N instance
	 */
	protected i18n: I18N;

	/**
	 * The event aggregator instance
	 */
	protected eventAggregator: EventAggregator;

	/**
	 * A list of event subscriptions to automatically dispose() when component unbind() is called.
	 */
	protected eventSubscriptions: EventSubscription[] = [];

	/**
	 * Aurelia store
	 */
	protected store?: Store<StateHistory<AppState>>;

	/**
	 * Length of log prefix, usually the caller's class name
	 */
	protected logPrefixWidth: number = 20;

	/**
	 * The view model's state (a sub-state of `AppState`)
	 * This property's type should be overwritten by the child class
	 */
	private state: StateHistory<AppState>;

	protected unbind(): void {
		this.eventSubscriptions.forEach((e) => e.dispose && e.dispose());
		this.eventSubscriptions = [];
	}

	/**
	 * Standardized eventAggregator publisher
	 *
	 * @param {string}	eventId	Event ID
	 * @param {any}	data	Event payload
	 */
	protected publishEvent(eventId: string, data?: any) {
		if (this.eventAggregator) {
			Logger.log('Publishing event:', eventId);
			this.eventAggregator.publish(eventId, data);
		} else {
			if (DEVELOPMENT) {
				throw new Error(
					'eventAggregator should be declared before publishing an event',
				);
			}
			Logger.warn(
				'eventAggregator should be declared before publishing an event',
			);
		}
	}
	/**
	 * Subscribe to an event
	 */
	protected subscribeToEvent(
		eventID: string,
		callback: (data?: any) => void,
	) {
		if (this.eventAggregator) {
			this.eventSubscriptions.push(
				this.eventAggregator.subscribe(eventID, callback),
			);
		} else {
			if (DEVELOPMENT) {
				throw new Error(
					'eventAggregator should be declared before subscribing to an event',
				);
			}
			Logger.error(
				'eventAggregator should be declared before subscribing to an event',
			);
		}
	}

	/**
	 * Subscribe once. The optional conditionPredicate parameter specifies a condition under which the subscription is
	 * valid. Thus, if the condition is met, the callback is called, otherwise the subscription is kept.
	 */
	protected subscribeToEventOnce(
		eventID: string,
		callback: (data?: any) => void,
		conditionPredicate?: (eventData) => boolean,
	) {
		if (this.eventAggregator) {
			let wrappedCallback;
			if (conditionPredicate) {
				wrappedCallback = (eventData) => {
					if (conditionPredicate(eventData)) {
						return callback(eventData);
					} else {
						this.eventSubscriptions.push(
							this.eventAggregator.subscribeOnce(
								eventID,
								wrappedCallback,
							),
						);
					}
				};
			} else {
				wrappedCallback = callback;
			}

			this.eventSubscriptions.push(
				this.eventAggregator.subscribeOnce(eventID, wrappedCallback),
			);
		} else {
			Logger.error(
				'eventAggregator should be declared before subscribing to an event',
			);
		}
	}

	/**
	 * Subscribe to state changes
	 */
	protected subscribeToStateChanges = (
		{
			callback,
			requireLoggedIn,
		}: {
			callback?: (stateHistory: StateHistory<AppState>) => void;
			requireLoggedIn: boolean;
		} = { requireLoggedIn: true },
	) => {
		this.store.state.subscribe((stateHistory: StateHistory<AppState>) => {
			this.state = stateHistory;
			// If login is required and we're not logged in, don't even bother calling the callback
			// So we prevent unnecessary reaction to state changes
			if (requireLoggedIn && !this.state.present.isLoggedIn) {
				return;
			}
			if (callback) {
				callback.call(this, stateHistory);
			}
		});
	};

	/**
	 * Dispatch an action via `aurelia-store`
	 * TODO: Add parameter type
	 */
	protected dispatch = (action, ...params): Promise<void> => {
		if (!this.store) {
			this.store = Container.instance.get<StateHistory<AppState>>(Store);
		}
		if (this.store && this.store.dispatch) {
			return this.store.dispatch(action, ...params);
		} else {
			Logger.warning('Unable to dispatch action');
			if (DEVELOPMENT) {
				throw new Error('Unable to dispatch action.');
			}
			return Promise.resolve();
		}
	};

	/**
	 * Get the app state
	 */
	@computedFrom('state')
	get appState(): StateHistory<AppState> {
		return this.state;
	}

	/**
	 * showNavigation is a computed property that exists to hide the
	 * navigation bar when WebUI is viewed in TreeView.
	 */
	@computedFrom(
		'router.currentInstruction.config.settings.data.hideNavigation',
		'router.currentInstruction.queryParams',
	)
	get showNavigation(): boolean {
		let hideNavigation: boolean | undefined;
		let navHide: string | undefined;
		if (this.router && this.router.currentInstruction) {
			if (this.router.currentInstruction.config.settings.data) {
				hideNavigation =
					this.router.currentInstruction.config.settings.data
						.hideNavigation;
			}
			if (this.router.currentInstruction.queryParams) {
				navHide =
					this.router.currentInstruction.queryParams['nav-hide'];
			}
		}
		return (
			// `hideNavigation` override set via router configuration in `app-routes.ts`
			// Default for either would mean not showing the navigation
			!hideNavigation &&
			!navHide &&
			sessionStorage.getItem(TREE_VIEW_SESSION_VARIABLE_NAME) !== '1'
		);
	}
}
